import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

/**
 * Bronson button component.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-buttons.html
 *
 * @param {boolean} [action] - Display button as the Action variant.
 * @param {boolean} [ariaExpanded] - Set `[aria-expanded]` attribute.
 * @param {string} [ariaLabel] - Set `[aria-label]` attribute.
 * @param {React.ReactNode} [children] - The button label.
 * @param {string} [className] - Further class names.
 * @param {boolean} [disabled] - Disable button.
 * @param {string} element - A custom HTML tag name to use for the button element.
 * @param {boolean} [full] - Display button with full width.
 * @param {boolean} [hiddenLabel] - Hides button label.
 * @param {string} [icon] - Path to button icon.
 * @param {string} [iconClassName] - Additional className on the button icon element.
 * @param {boolean} [iconReversed] - Determines, if icon should be displayed on the right side.
 * @param {boolean} [iconViaCss] - Determines, if icon should be added via CSS.
 * @param {boolean} [incognito] - Display button with in the label-revealing variant.
 * @param {boolean} [inContainer] - Determines if button lies in container.
 * @param {string} [link] - Link, that the button refers to.
 * @param {function} onClick - Defines onClick event.
 * @param {boolean} [round] - Display round button.
 * @param {boolean} [secondary] - Secondary button.
 * @param {boolean} [simple] - Simple button link.
 * @param {boolean} [small] - Display small button.
 * @param {string} [title] - Button title.
 * @param {boolean} [truncate] - Truncate long button link text.
 * @param {string} [testId] - The `[data-testid]` attribute on the component element.
 * @param {object} [otherProps] - Other props passed to the component element.
 * @returns {JSX.Element} - The Button component.
 * @constructor
 */
export function Button({
  action,
  ariaExpanded,
  ariaLabel,
  children,
  className,
  disabled,
  element = 'button',
  full,
  hiddenLabel,
  icon,
  iconClassName,
  iconReversed,
  iconViaCss,
  incognito,
  inContainer,
  link,
  onClick,
  round,
  secondary,
  simple,
  small,
  title,
  truncate,
  testId,
  ...otherProps
}) {
  /**
   * This assigns the `element` (`button-element` in Bronson) prop to a variable
   * which is in PascalCase, because custom component tags names must start with
   * an upperCase letter as lower-case tags are reserved for HTML.
   * @see https://reactjs.org/docs/jsx-in-depth.html#html-tags-vs.-react-components
   * @type {string}
   * Remove the {@link otherProps?.normalLink} part as this is because
   * of the switch from Link to Button Link in BRON-10612. Remove in v8.0.0.
   */
  // eslint-disable-next-line no-nested-ternary
  const CustomElement = otherProps?.normalLink ? 'a' : typeof element === 'string' ? `${element}` : element

  /**
   * @deprecated
   * Remove the {@link otherProps?.normalLink} part as this is because
   * of the switch from Link to Button Link in BRON-10612. Remove in v8.0.0.
   */
  if (otherProps?.normalLink) {
    // eslint-disable-next-line no-param-reassign
    delete otherProps.normalLink
  }

  const buttonClassNameList = classNames(
    'c-btn',
    {
      'c-btn--icon': icon && !link,
      'c-btn--icon-only': icon && (!children || hiddenLabel),
      // Convenience props from Bronson variants.
      'c-btn--secondary': secondary,
      'c-btn--action': action,
      'c-btn--full': full,
      'c-btn--incognito': incognito,
      'c-btn--link': link,
      'c-btn--small': small,
      'c-btn--round': round,
      'c-btn--simple': simple,
      'c-btn--truncate': truncate,
      // Do not apply reverse logic on links. They are reversed automatically.
      'c-btn--reversed': icon && !link && iconReversed,
      // Convenience prop when Button is used inside {@link ButtonContainer}.
      'o-button-container__button': inContainer,
    },
    className
  ).trim()

  const iClassNameList = classNames(
    'c-btn__icon',
    {
      'c-icon': !iconViaCss,
      [`c-icon--[${icon}]`]: !iconViaCss,
    },
    iconClassName
  ).trim()

  function renderIfButtonIcon() {
    if (icon) {
      return <i className={iClassNameList} aria-hidden="true" role="img" />
    }
    return null
  }

  // generated
  const spanClassNameList = classNames({
    'c-btn__text ': true,
    'u-visually-hidden ': hiddenLabel,
  }).trim()

  // generated
  function renderIfButtonLabel() {
    if (children) {
      return <span className={spanClassNameList}>{children}</span>
    }
    return null
  }

  return (
    <CustomElement
      aria-disabled={disabled && 'true'}
      aria-expanded={ariaExpanded}
      aria-label={ariaLabel}
      className={buttonClassNameList}
      data-testid={testId}
      disabled={disabled}
      onClick={onClick}
      /**
       * Conditionally set the title when either the `title` prop is set
       * or the children are a plain string.
       */
      title={title || typeof children === 'string' ? title || children : null}
      {...otherProps}
    >
      <>
        {renderIfButtonLabel()}
        {renderIfButtonIcon()}
      </>
    </CustomElement>
  )
}

Button.propTypes = {
  ariaExpanded: PropTypes.bool, // Bronson template: 'button-attribute'.
  ariaLabel: PropTypes.string, // Bronson template: 'button-attribute'.
  children: PropTypes.node, // Bronson template: 'button-label'.
  className: PropTypes.string, // Bronson template: 'button-modifier'.
  disabled: PropTypes.bool, // Bronson template: 'button-attribute'.
  element: PropTypes.oneOfType([PropTypes.string, PropTypes.node, PropTypes.func]), // Bronson template: 'button-element'.
  hiddenLabel: PropTypes.bool, // Bronson template: 'button-hidden-label'.
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]), // Bronson template: 'button-icon'.
  iconClassName: PropTypes.string, // Bronson template: 'button-icon-class'.
  iconReversed: PropTypes.bool, // Bronson template: 'button-icon-reversed'.
  iconViaCss: PropTypes.bool, // Bronson template: 'button-icon-via-css'.
  inContainer: PropTypes.bool,
  labelClassName: PropTypes.string, // Bronson template: 'button-label-class'.
  onClick: PropTypes.func, // Bronson template: 'button-href'.
  testId: PropTypes.string, // Added for `[data-testid]` attribute.
  title: PropTypes.string, // Bronson template: 'button-title'.
  /* Convenience props */
  action: PropTypes.bool, // Convenience prop for `.c-btn--action` (Bronson template: 'button-modifier').
  full: PropTypes.bool, // Convenience prop for `.c-btn--full` (Bronson template: 'button-modifier').
  incognito: PropTypes.bool, // Convenience prop for `.c-btn--incognito` (Bronson template: 'button-modifier').
  link: PropTypes.bool, // Convenience prop for `.c-btn--link` (Bronson template: 'button-modifier').
  round: PropTypes.bool, // Convenience prop for `.c-btn--round` (Bronson template: 'button-modifier').
  secondary: PropTypes.bool, // Convenience prop for `.c-btn--secondary` (Bronson template: 'button-modifier').
  simple: PropTypes.bool, // Convenience prop for `.c-btn--simple` (Bronson template: 'button-modifier').
  small: PropTypes.bool, // Convenience prop for `.c-btn--small` (Bronson template: 'button-modifier').
  truncate: PropTypes.bool, // Convenience prop for `.c-btn--truncate` (Bronson template: 'button-modifier').
  /** Deprecated Props from {@link Link} */
  normalLink: PropTypes.bool, // Remove in v8.0.0.
}
